import React from "react";
import { Typography as MuiTypography, TypographyProps } from "@material-ui/core";
import clsx from "clsx";
import { TypographyModel, TypographyVariantModel } from "./tools/Typography.model";

import useStyles from "./Typography.style";

/**
 * MUI v4 Typography supports only a fixed set of runtime variants (e.g. body1, body2, h1...).
 * Custom design-system variants (e.g. "subHeadline2") are not recognized at runtime and would
 * trigger prop-types warnings or require unsafe internal overrides.
 *
 * To keep a clean API for our design system while staying compatible with MUI v4, we map
 * custom semantic variants to the closest supported MUI variants.
 *
 * This allows us to:
 * - keep design-system-friendly naming (e.g. subHeadline2)
 * - avoid runtime warnings from MUI prop-types
 * - stay compatible with MUI v4 limitations
 * - keep an easy migration path to MUI v5, where custom variants are supported natively
 */

const variantMap: Record<string, TypographyProps['variant']> = {
  subHeadline2: 'body2',
  special: 'caption',
};

const resolveVariant = (
  variant: TypographyVariantModel
): TypographyProps['variant'] => {
  return variantMap[variant] ?? (variant as TypographyProps['variant']);
};

export const Typography = ({
  children,
  testId = "Typography",
  textColor,
  fontWeight = 'normal',
  variant = 'body1',
  component,
  className,
  ...props
}: TypographyModel) => {
  const c = useStyles({ textColor, fontWeight, variant });
  const resolvedVariant = resolveVariant(variant);
  return (
    <MuiTypography
      {...props}
      className={clsx(c.root, className)}
      data-testid={testId}
      data-type="ui/Typography"
      variant={resolvedVariant}
      variantMapping={{ [resolvedVariant as string]: component }}
    >
      {children}
    </MuiTypography>
  );
};
